import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Snackbar
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Parse from 'parse';
import CustomPagination from '../../components/CustomPagination';

// Normalize a raw category value (e.g. 'Slot' / 'slot') to a stable display label.
const normalizeCategory = (c) => (c ? c.charAt(0).toUpperCase() + c.slice(1).toLowerCase() : c);

const formatDate = (val) => {
  if (!val) return '-';
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString();
};

// Daily-coins amounts are decimal currency units (summed raw bet/win), NOT cents.
const formatAmt = (val) => (val != null ? Number(val).toFixed(2) : '0.00');

// When no date range is chosen, default to a recent window so the daily
// aggregation stays fast (a full-history scan over ~8M rows is very slow).
const DEFAULT_RANGE_DAYS = 30;
const defaultStartDate = () =>
  new Date(Date.now() - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const AgentDailyCurrencyReport = () => {
  const todayStr = new Date().toISOString().split('T')[0];

  const [games, setGames] = useState([]);
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [showExportSnackbar, setShowExportSnackbar] = useState(false);

  // Load the operator's games for the Game / Game Type dropdowns and to resolve
  // each game's category (Game Type) by name.
  useEffect(() => {
    const loadGames = async () => {
      try {
        const sessionToken = localStorage.getItem('sessionToken');
        const result = await Parse.Cloud.run('rgsListGamesForDropdown', {}, { sessionToken });
        if (result?.games) setGames(result.games);
      } catch {
        setGames([]);
      }
    };
    loadGames();
  }, []);

  const categoryByName = games.reduce((acc, g) => {
    if (g.name) acc[g.name.toLowerCase()] = normalizeCategory(g.category);
    return acc;
  }, {});
  const resolveGameType = (gameName) => categoryByName[(gameName || '').toLowerCase()] || '-';

  const buildFilter = () => {
    const filter = {};
    if (startDate) filter.startDate = startDate;
    if (endDate) filter.endDate = endDate;
    if (gameNameFilter.trim()) filter.gameName = gameNameFilter.trim();
    return filter;
  };

  const fetchData = async (customPage = 1) => {
    setLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');
      const filter = buildFilter();

      if (!filter.startDate && !filter.endDate && !filter.gameName) {
        filter.startDate = defaultStartDate();
        filter.endDate = todayStr;
      }

      const result = await Parse.Cloud.run(
        'getDailyOperatorCoins',
        { page: customPage, perPage, operatorId, field: 'id', order: 'DESC', filter },
        { sessionToken }
      );

      const rows = result.data || [];

      setData(rows);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Daily report error:', err);
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setHasSearched(true);
    fetchData(1);
  };

  const handleClear = () => {
    setGameNameFilter('');
    setStartDate('');
    setEndDate('');
    setData([]);
    setTotal(0);
    setHasSearched(false);
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportSnackbar(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const operatorId = localStorage.getItem('operatorId');
      const filter = buildFilter();
      if (!filter.startDate && !filter.endDate && !filter.gameName) {
        filter.startDate = defaultStartDate();
        filter.endDate = todayStr;
      }

      const result = await Parse.Cloud.run(
        'getDailyOperatorCoins',
        { page: 1, perPage: 10000, operatorId, field: 'id', order: 'DESC', filter },
        { sessionToken }
      );

      const rows = result.data || [];

      const exportData = rows.map((r, i) => {
        setExportProgress(Math.floor((i / rows.length) * 80) + 10);
        const netWin = r.totalBetAmount - r.totalWinAmount;
        const totalLoss = Math.max(0, r.totalWinAmount - r.totalBetAmount);
        return {
          Date: formatDate(r.startTime || r.date),
          'Game Name': r.gameName || '-',
          'Game Type': resolveGameType(r.gameName),
          'Total Bet': formatAmt(r.totalBetAmount),
          'Total Win': formatAmt(r.totalWinAmount),
          'Total Loss': formatAmt(totalLoss),
          'Net Win': formatAmt(netWin),
          'RTP %': (r.rtp || 0).toFixed(2),
          'Total Spins': r.sessionCount || 0
        };
      });

      setExportProgress(95);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Agent Daily Currency Report');
      const xlsData = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      setExportProgress(100);
      saveAs(
        new Blob([xlsData], { type: 'application/octet-stream' }),
        'AgentDailyCurrencyReport.xlsx'
      );
      setTimeout(() => setShowExportSnackbar(false), 800);
    } catch (err) {
      alert('Export failed: ' + err.message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Snackbar
        open={showExportSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={null}
      >
        <Alert severity="info" icon={false} sx={{ px: 2 }}>
          <Typography variant="body2" mb={0.5}>
            Exporting... {exportProgress}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={exportProgress}
            sx={{ width: 260, height: 6, borderRadius: 5 }}
          />
        </Alert>
      </Snackbar>

      {/* Search Filters */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Typography sx={{ color: '#FFC107', fontWeight: 600, fontSize: 15, mb: 2 }}>
          Search Filters
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          <FormControl size="small" sx={{ minWidth: 200, ...selectContainerSx }}>
            <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Game Name
            </InputLabel>
            <Select
              label="Game Name"
              notched
              displayEmpty
              value={gameNameFilter}
              onChange={(e) => setGameNameFilter(e.target.value)}
              sx={selectSx}
            >
              <MenuItem value="">All Games</MenuItem>
              {games.map((g) => (
                <MenuItem key={g.id} value={g.name}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <MuiTextField
            size="small"
            type="date"
            label="Date From"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: todayStr }}
            sx={fieldSx}
          />

          <MuiTextField
            size="small"
            type="date"
            label="Date To"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate || undefined, max: todayStr }}
            sx={fieldSx}
          />

          <Button variant="contained" onClick={handleSearch} disabled={loading} sx={applyBtnSx}>
            {loading ? <CircularProgress size={18} sx={{ color: '#000' }} /> : 'Search'}
          </Button>

          <Button variant="outlined" onClick={handleClear} disabled={loading} sx={clearBtnSx}>
            Clear
          </Button>

          <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={isExporting || !hasSearched || data.length === 0}
            sx={applyBtnSx}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Daily Report Table */}
      <Box sx={{ backgroundColor: '#242424', borderRadius: 2, p: 2 }}>
        <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#2A2A2A' }}>
                {[
                  'Date',
                  'Game Name',
                  'Game Type',
                  'Total Bet',
                  'Total Win',
                  'Total Loss',
                  'Net Win',
                  'RTP %',
                  'Total Spins'
                ].map((col, i) => (
                  <TableCell
                    key={col}
                    align={i < 3 ? 'left' : 'right'}
                    sx={{ color: '#FFC107', fontWeight: 600, py: 1.5, whiteSpace: 'nowrap' }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFC107' }} />
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#fff' }}>
                    {hasSearched
                      ? 'No data found for the selected filters.'
                      : 'Apply filters and click Search to view the daily report.'}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => {
                  const netWin = (row.totalBetAmount || 0) - (row.totalWinAmount || 0);
                  const totalLoss = Math.max(
                    0,
                    (row.totalWinAmount || 0) - (row.totalBetAmount || 0)
                  );
                  return (
                    <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#2A2A2A' } }}>
                      <TableCell sx={{ color: '#fff', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatDate(row.startTime || row.date)}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 12 }}>
                        {row.gameName || '-'}
                      </TableCell>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                        {resolveGameType(row.gameName)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {formatAmt(row.totalBetAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50', fontSize: 12 }}>
                        {formatAmt(row.totalWinAmount)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: totalLoss > 0 ? '#f44336' : 'rgba(255,255,255,0.5)',
                          fontSize: 12
                        }}
                      >
                        {formatAmt(totalLoss)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: netWin >= 0 ? '#4caf50' : '#f44336', fontSize: 12 }}
                      >
                        {formatAmt(netWin)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#FFC107', fontSize: 12 }}>
                        {(row.rtp || 0).toFixed(2)}%
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#fff', fontSize: 12 }}>
                        {row.sessionCount || 0}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={(p) => {
                setPage(p);
                fetchData(p);
              }}
              setPerPage={() => {}}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': { color: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FFC107'
  }
};

const selectContainerSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' }
};

const selectSx = {
  color: '#fff',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFC107' },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' }
};

const applyBtnSx = {
  background: '#FFC107',
  color: '#0A0A0A',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': { backgroundColor: '#FFA000' },
  '&:disabled': { backgroundColor: 'rgba(255,193,7,0.3)', color: 'rgba(0,0,0,0.4)' }
};

const clearBtnSx = {
  borderColor: '#FFC107',
  color: '#FFC107',
  textTransform: 'none',
  '&:hover': { borderColor: '#FFA000', color: '#FFA000', backgroundColor: 'rgba(255,193,7,0.08)' }
};

export default AgentDailyCurrencyReport;
